<template>
  <va-modal
    :value="isOpen"
    id="endAppointmentModal"
    ref="endAppointmentModal"
    size="medium"
    :showOkay="true"
    :show-cancel="true"
    :cancelText="$t('modals.back')"
    @cancel="onCancel"
    @ok="endAppointment"
    :ok-text="$t('modals.buttons.confirm')"
    >

    <div slot="header">
      <span class="modal-title">{{ $t('clinical.appointments.end_appointment') }}</span>
    </div>

    <div class="evollu-form py-3">
      <div class="row" v-if="appointment.person">
        <div class="col-12">
          <label>{{ $t('clinical.people.name') }}:&nbsp;</label>
          <span class="is-bold">{{ appointment.person.name }}</span>
        </div>

        <div class="col-12">
          <label>{{ $t('clinical.people.client_nr') }}:&nbsp;</label>
          <span class="is-bold">{{ appointment.person.process_id || 'N/A' }}</span>
        </div>

        <span class="col-12">
          <i class="fa fa-clock-o is-bold" style="margin-right: 2px; margin-bottom: 0.7rem;"></i>
          <span class="is-bold"> {{ appointment.start | localTime('DD MMMM YYYY [&nbsp;] HH:mm') }} &nbsp; - &nbsp; {{ appointment.end | localTime('HH:mm') }}</span>
        </span>
      </div>

      <div class="row">
        <div class="col-12 col-sm-6">
          <DatePicker
            :id="'EndAppointmentDatePicker'"
            :label="$t('clinical.appointments.appointment_end_date')"
            :config="config"
            :clearable="false"
            :error="form.errors.has('end')"
            :errorMessages="[form.errors.get('end')]"
            @input="form.errors.clear('end')"
            v-model="form.data.end"
          />
        </div>
        <div class="col-12">
          <small>({{ $t('clinical.people.actual_beginning') }}:
          {{ appointment.started_at | localTime('HH:mm') }})</small>
        </div>
      </div>
    </div>

  </va-modal>
</template>

<script>
import DatePicker from '@/components/common/DatePicker'
import { AppointmentService } from '@/api/AuthClinicalService'
import Errors from '@/utils/StandardErrors'
export default {
  props: {
    appointment: {
      type: Object,
      required: true
    },
    component: {
      type: Object,
      required: true
    }
  },
  components: {
    DatePicker
  },
  data: function () {
    return {
      nextAppointmentDate: null,
      isOpen: false,
      form: {
        data: {
          end: null
        },
        errors: new Errors()
      },
      config: {
        minDate: this.moment(this.appointment.start).format('YYYY-MM-DD'),
        maxDate: this.moment(this.appointment.start).add(1, 'day').format('YYYY-MM-DD'),
        enableTime: true,
        time_24hr: true,
        noCalendar: false,
        dateFormat: 'Y-m-d H:i',
        minTime: this.moment().isBefore(this.moment(this.appointment.started_at)) ? this.moment() : this.$options.filters.localTime(this.appointment.started_at, 'HH:mm'),
        maxTime: '23:59'
      }
    }
  },
  mounted: function () {
    this.isOpen = true
    AppointmentService.nextStartedAppointment(this.component.service.url, this.appointment.id)
      .then(nextApp => {
        if (nextApp && nextApp.started_at) {
          this.nextAppointmentDate = this.$options.filters.localTime(nextApp.started_at)
        }
        let currentDate = this.moment()
        let currentAppointmentStarted = this.moment(this.$options.filters.localTime(this.appointment.started_at))
        let currentAppointmentEnd = this.moment(this.$options.filters.localTime(this.appointment.end))
        let currentAppointmentEndThreshold = currentAppointmentEnd.clone().add(30, 'minutes')
        let nextAppointmentMomentDate = this.moment(this.nextAppointmentDate)
        // let nextAppointmentMomentDateThreshold = nextAppointmentMomentDate.add(30, 'minutes')
        // 1st Scenario: The current appointment is being finished within the schedule so we can suggest the current date/time
        if ((currentDate.isAfter(currentAppointmentStarted) && currentDate.isBefore(currentAppointmentEnd)) || currentDate.isBefore(currentAppointmentStarted)) {
          this.form.data.end = currentDate.format('YYYY-MM-DD HH:mm')
        } else if (this.nextAppointmentDate) {
          // 2nd Scenario: The current appointment is being finished after the expected end date and within 30 minutes with other appointment started
          // and the next appointment started within those 30 minutes threshold. We suggest the startedDate of the next appointment
          if (currentDate.isAfter(currentAppointmentEnd) && currentDate.isBefore(currentAppointmentEndThreshold) && nextAppointmentMomentDate.isBefore(currentAppointmentEndThreshold)) {
            this.form.data.end = nextAppointmentMomentDate.format('YYYY-MM-DD HH:mm')
          } else {
            // else, we suggest the current appointment end date.
            this.form.data.end = currentAppointmentEnd.format('YYYY-MM-DD HH:mm')
          }
        } else { // if there is no next appointment
          // 3rd Scenario: The current appointment is being finished after the expected end date and there is no other appointment started
          // and it is in threshold we suggest the current date
          if (currentDate.isAfter(currentAppointmentEnd) && currentDate.isBefore(currentAppointmentEndThreshold)) {
            this.form.data.end = currentDate.format('YYYY-MM-DD HH:mm')
          } else { // else, we suggest the current appointment end date.
            const duration = this.moment.duration(currentAppointmentEnd.diff(this.moment(this.$options.filters.localTime(this.appointment.start))))
            const suggestedEndDate = currentAppointmentStarted.clone().add(duration.asMinutes(), 'minutes')
            console.log(currentDate.isAfter(suggestedEndDate), currentDate, suggestedEndDate)
            if (suggestedEndDate.isAfter(currentDate)) {
              this.form.data.end = currentDate.format('YYYY-MM-DD HH:mm')
            } else {
              this.form.data.end = suggestedEndDate.format('YYYY-MM-DD HH:mm')
            }
          }
        }
      }).catch(err => console.log(err))
  },
  methods: {
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('modal-cancel')
      }, 500)
    },
    validateForm: function () {
      if (!this.form.data.end) {
        this.form.errors.set('end', this.$t('clinical.appointments.errors.the_field_appointment_end_is_required'))
      }
      return !this.form.errors.any()
    },
    endAppointment: function () {
      if (this.validateForm()) {
        const data = {
          end: this.$options.filters.utcTime(`${this.form.data.end}:00`)
        }

        AppointmentService.end(this.component.service.url, this.appointment.id, data)
          .then(response => {
            this.$store.commit(`waiting-room-${this.component.service.slug}/REMOVE_ACTIVE_APPOINTMENT`, { appointment: this.appointment })

            if (response.data.error) {
              this.$emit('modal-end-appointment-error')
            }
          })
          .catch(errors => console.log(errors))
          .finally(() => {
            this.$emit('modal-end-appointment-completed')
          })
      }
    },
  }
}
</script>
